/**
 * Maps Firebase Auth error codes to user-friendly messages.
 * Use `toAuthMessage(err)` in catch blocks to surface readable feedback.
 */

const MESSAGES: Record<string, string> = {
  'auth/invalid-email': 'That email address looks invalid.',
  'auth/user-disabled': 'This account has been disabled. Contact support.',
  'auth/user-not-found': 'No account found with that email.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/weak-password': 'Password is too weak — use at least 6 characters.',
  'auth/missing-password': 'Please enter a password.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/network-request-failed': 'Network error. Check your connection and try again.',
  'auth/requires-recent-login': 'Please re-authenticate to complete this action.',
  'auth/expired-action-code': 'This link has expired. Request a new one.',
  'auth/invalid-action-code': 'This link is invalid or has already been used.',
  'auth/popup-closed-by-user': 'The sign-in popup was closed before completing.',
  'auth/operation-not-allowed': 'This sign-in method is not enabled for the project.',
  'auth/unverified-email': 'Please verify your email before continuing.',
};

interface FirebaseLikeError {
  code?: string;
  message?: string;
}

export function toAuthMessage(err: unknown): string {
  const e = err as FirebaseLikeError;
  if (e?.code && MESSAGES[e.code]) return MESSAGES[e.code];
  // Fall back to Firebase's raw message, stripped of the "Firebase: ... (auth/x)" noise.
  if (e?.message) return e.message.replace(/^Firebase:\s*/, '').replace(/\s*\(auth\/[^)]+\)\.?$/, '');
  return 'Something went wrong. Please try again.';
}

/** Wraps an async auth op so its rejection is always a friendly Error. */
export async function withAuthError<T>(op: () => Promise<T>): Promise<T> {
  try {
    return await op();
  } catch (err) {
    throw new Error(toAuthMessage(err));
  }
}
